// Trek Light customizations for the Offerly "Pay What You Can" modal.
//
// Offerly injects #OfferlyModalDiv into the page and (re)builds the form while
// the modal opens, and its button handler stops event propagation — so all
// changes must hook the capture phase and re-apply until the form settles.

// PWYC experiment B2a (2026-08-19): the offer-amount field is no longer
// hidden while a preset pill is selected. It stays visible carrying the
// selected pill's amount and remains editable; typing a value that does not
// match the pressed pill clears the selection. B2's own +7d order-level read
// (Aug 3-9 vs Aug 10-16, n=97/62) found $50-150 list carts down ~76% and 3-4
// unit carts down 78%, while single-unit carts fell only 32% in line with
// store-wide softness, and minimum realized-of-list stayed 0.500 in BOTH
// windows — so the ladder floor was never the binding constraint. Hiding the
// field put custom amounts behind a second click labelled "Submit A Request",
// which reads as review-and-wait rather than checkout. This restores the
// pre-B2 affordance without giving up the full-price anchor or the pills.
//
// PWYC experiment B2 (2026-08): replace the blank Desired Price field with
// preset price pills — Full price (preselected), ~80% of list, and a lowest
// pill that never sits below the product line's Offerly auto-approve floor
// (a pill we offer must never produce a counter-offer). A "Submit A Request"
// pill reveals the original free-entry field: honest framing, since custom
// offers below the floor genuinely go to review instead of instant checkout.
// The store runs Offerly with useTotalAmount, so every pill amount is
// unit price x quantity and re-renders when quantity changes.

// Percent-of-list tiers per product line. floorPct mirrors the max discount
// configured in Offerly's auto-accept rules (backend still enforces; this
// only decides which numbers we display). Deliberate rule from Seth: the
// LOWEST pill sits strictly ABOVE the floor (floorPct + TL_PILL_BUFFER), so
// the pills never reveal the real auto-accept minimum — customers who type
// a lower amount in the request path still auto-approve, they just aren't
// invited to. Keys are matched as lowercase substrings of the product type.
// floorPct = customer-pays fraction at Offerly's auto-accept limit, read from
// the Offerly admin 2026-08-09: main 8-collection group (incl. adult + kids
// hats, mugs, hammocks) auto-accepts up to 35% off => floor 0.65; Mystery
// Blanket 36% off => 0.64; a fourth group 41% off => 0.59 (collections
// unconfirmed); Canvas Flags 73% off => 0.27 (closeout — pills stay at the
// default steps, the floor only ever pushes pills UP). Default holds the most
// conservative common floor so unmapped types can't show an unsafe pill.
// Pay It Forward: amounts ABOVE list skip Offerly entirely — the product goes
// in the cart at full price plus N units of the $1 Pay It Forward variant
// covering the overage (Shopify can't price above list, so generosity is its
// own line item). PIF-001, created 2026-08-09.
const TL_PIF_VARIANT_ID = 44694003875883;

// Step ladders (Seth-approved direction 2026-08-09): full-price anchor stays,
// the two lower pills sit HIGH (14%/25% off) so the pills never push anyone
// toward the deep end. High-AOV lines (blankets, hammocks) run a tighter
// ladder (12%/22%) because percent gaps turn into real dollars there.
const TL_PILL_BUFFER = 0.06;
const TL_PWYC_TIERS = {
  hat:     { steps: [1.0, 0.86, 0.75], floorPct: 0.65 },
  blanket: { steps: [1.0, 0.88, 0.78], floorPct: 0.64 },
  hammock: { steps: [1.0, 0.88, 0.78], floorPct: 0.65 },
  flag:    { steps: [1.0, 0.86, 0.75], floorPct: 0.27 },
  default: { steps: [1.0, 0.86, 0.75], floorPct: 0.65 },
};

function tlProductType() {
  const meta = window.meta && window.meta.product;
  return ((meta && meta.type) || 'default').toLowerCase();
}

// A bundle whose floor exceeds list price has NO valid offer range
// (even full price would be refused by the rewrite task), so the emitted tag
// carries pwycDisabled and the Offerly entry points are hidden outright.
// Runs once at parse time; the style tag wins over Offerly's late injection.
(function tlPwycDisableCheck() {
  const el = document.querySelector('script[data-tl-pwyc-floor]');
  if (!el) return;
  try {
    if (JSON.parse(el.textContent).pwycDisabled === true) {
      const style = document.createElement('style');
      style.textContent = '.offerButton, #OfferlyModalDiv { display: none !important; }';
      document.head.appendChild(style);
    }
  } catch (e) { /* malformed tag: leave Offerly alone */ }
})();

function tlTiersForProduct() {
  // Per-product floor override, emitted by snippets/ml-bundle-preorder.liquid
  // on EB bundle pages from custom.pwyc_floor_pct. That metafield is a
  // fraction of the PARENT's own displayed price - the same base Offerly's
  // auto-accept and auto-counter rules and the Mechanic rewrite task use - so
  // the snippet emits it unchanged and this is a like-for-like floor.
  // It is an AUTO-accept floor only: a person accepting an offer in the
  // Offerly admin may go below it, and the rewrite task must honour that
  // (see the 2026-09-19 note in ml-bundle-preorder.liquid).
  // Bundles otherwise fall through to 'default', which happens to equal the
  // hat tier: accidentally right for twinsies, wrong for any mixed-margin
  // bundle. Steps keep the default ladder; tlPillAmounts clamps each step to
  // floorPct + TL_PILL_BUFFER, so a higher floor lifts the pills on its own.
  const floorEl = document.querySelector('script[data-tl-pwyc-floor]');
  if (floorEl) {
    try {
      const f = JSON.parse(floorEl.textContent).floorPct;
      if (typeof f === 'number' && f > 0 && f <= 1) {
        return { steps: TL_PWYC_TIERS.default.steps, floorPct: f };
      }
    } catch (e) {
      // Malformed tag: fall through to the type map rather than break pills.
    }
  }
  const type = tlProductType();
  for (const key of Object.keys(TL_PWYC_TIERS)) {
    if (key !== 'default' && type.includes(key)) return TL_PWYC_TIERS[key];
  }
  return TL_PWYC_TIERS.default;
}

function tlModalUnitPrice() {
  const priceEl = document.getElementById('modalPrice') || document.getElementById('mobileModalPrice');
  const match = priceEl && priceEl.textContent.match(/\$\s*([\d,]+(?:\.\d{1,2})?)/);
  if (!match) return null;
  const price = parseFloat(match[1].replace(/,/g, ''));
  // A price of 0 is a PARSE SUCCESS but not a usable price. Offerly renders
  // "$0" transiently while it builds the modal, and the old `price === null`
  // guard let that through: every tier computed to 0, the de-dupe collapsed
  // [0,0,0] to a single [0], and tlRenderPills baked a one-pill "$0" ladder
  // that never recovered, because only a quantity change re-renders. Selecting
  // it then put 0 in the amount field. An EMPTY element was already safe (no
  // match -> null); zero was the hole. Reject anything non-positive here, at
  // the single source, so every caller inherits the guard.
  if (!(price > 0)) return null;
  // Remember the last good list price so PIF mode can restore the price line
  // without depending on Offerly's <s> markup being present. Cache it on BOTH
  // price elements, not just the one we read: Offerly ships #modalPrice AND
  // #mobileModalPrice in the same DOM and hides one by CSS, so this function
  // always reads #modalPrice (first in the || chain) while a phone actually
  // DISPLAYS #mobileModalPrice. Caching only the element we read would leave
  // the visible one with no cache, and the PIF restore would then depend on
  // Offerly appending its error rather than replacing the price text.
  // Verified on a 390x844 viewport: both elements present, #modalPrice hidden.
  [document.getElementById('modalPrice'), document.getElementById('mobileModalPrice')]
    .forEach((el) => { if (el) el.dataset.tlListPrice = String(price); });
  return price;
}

function tlQty() {
  const qtyInput = document.querySelector('input.offerInput[name="quantity"]');
  return Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
}

// Pill amounts: percent of list, clamped to the line floor, whole dollars.
function tlPillAmounts() {
  const price = tlModalUnitPrice();
  if (price === null) return null;
  const tiers = tlTiersForProduct();
  const qty = tlQty();
  // min(1, ...) caps the buffered floor at list price: a floor above 0.94
  // would otherwise push every pill above list, where tlPifIntercept treats
  // the submission as Pay It Forward instead of an offer (codex P2). A no-op
  // for every type-map tier (max buffered floor 0.71).
  const amounts = tiers.steps.map((pct) => {
    const used = Math.min(1, Math.max(pct, tiers.floorPct + TL_PILL_BUFFER));
    // A full-price pill must carry the EXACT list total: rounding $500.24
    // down to $500 puts the suggestion below a floor of 1.0 and the rewrite
    // task refuses the code the UI itself suggested (codex round 3). All
    // sub-list pills stay whole dollars as designed.
    return used >= 1 ? Math.round(price * qty * 100) / 100 : Math.round(price * used * qty);
  });
  // De-dupe (tiny prices can collapse tiers) while preserving order.
  return [...new Set(amounts)];
}

function tlInjectStylesOnce() {
  if (document.getElementById('tlPwycStyles')) return;
  const style = document.createElement('style');
  style.id = 'tlPwycStyles';
  style.textContent = [
    // Price section: the modal's core interaction gets the strongest visual
    // treatment — labeled section, three equal cards, unmistakable selection.
    '#tlPriceLabel{display:block;margin:14px 0 8px}',
    '#tlPwycPills{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 8px}',
    '#tlPwycPills .tl-pill{flex:1 1 0;font-family:"Archivo Narrow",Arial,sans-serif;',
    'font-weight:700;font-size:18px;letter-spacing:.5px;min-height:52px;',
    'padding:10px 4px;border-radius:10px;cursor:pointer;',
    'border:1.5px solid #d8cbba;background:#fff;color:#2b211b;',
    'transition:background .12s,border-color .12s,color .12s}',
    '#tlPwycPills .tl-pill[aria-pressed="true"]{background:#2b211b;border-color:#2b211b;color:#fffdf8}',
    '#tlPwycPills .tl-pill.tl-request{flex:100 1 100%;background:transparent;',
    'font-size:15px;min-height:44px;letter-spacing:.8px;text-transform:uppercase}',
    '#tlPwycPills .tl-pill.tl-request[aria-pressed="true"]{background:#2b211b;color:#fffdf8}',
    '#tlPwycPills .tl-pill:focus-visible{outline:2px solid #e84e28;outline-offset:2px}',
    '#OfferlyModalDiv #modalPrice,#OfferlyModalDiv #mobileModalPrice{color:#2b211b!important;',
    'font-size:17px!important;font-weight:700;line-height:1.3}',
    '#OfferlyModalDiv #mobileModalProductTitle,#OfferlyModalDiv #modalProductTitle,',
    '#OfferlyModalDiv #productTitleOfferModal{font-size:17px!important;font-weight:normal!important;',
    'color:#2b211b;line-height:1.25;padding-bottom:2px!important}',
    // 24px minimum tap target (WCAG 2.2 SC 2.5.8). The link measured 120x19
    // on a 390px viewport; the vertical padding makes up the difference and
    // the margin is trimmed so the surrounding spacing is unchanged.
    '#tlPifLink{display:block;background:none;border:none;padding:6px 8px;margin:0 auto 4px;',
    'font-size:13px;color:#6b5d52;text-decoration:underline;cursor:pointer;text-align:center}',
    '#tlPifLink:focus-visible{outline:2px solid #e84e28;outline-offset:2px}',
    '#tlPifHelper{font-size:13px;line-height:1.4;color:#6b5d52;margin:8px 0 0}',
    '#tlPifHelper b{color:#2b211b}',
    '.tl-pif-mode input.offerInput[name="offerAmount"]{border-color:#e84e28;background:#fff8f5}',
    '.tl-amount-wrap{position:relative}',
    '.tl-amount-wrap .tl-dollar{position:absolute;left:12px;',
    'transform:translateY(-50%);color:#2b211b;pointer-events:none}',
    // Mobile: the whole modal must fit without scrolling. The product photo
    // inside the modal is redundant (the PDP is right behind it) and is the
    // single biggest block; drop it and tighten every row.
    '@media (max-width: 480px){',
    // Modal surface always covers the whole viewport so the PDP can never
    // peek out beneath it (keyboard-driven scrolls included).
    '#OfferlyModalDiv .offerly-modal-content{min-height:100vh;min-height:100dvh}',
    // Compact product row: 52px thumbnail beside title/price instead of the
    // stacked 150px photo (Offerly inlines styles, hence the !importants).
    '#OfferlyModalDiv #productImageMobileDiv{display:flex;flex-direction:column;',
    'align-items:center;justify-content:center;gap:4px;padding:2px 0 6px}',
    '#OfferlyModalDiv #productImageMobileDiv img{height:52px!important;width:52px!important;',
    'object-fit:cover;border-radius:8px;padding-bottom:0!important}',
    '#OfferlyModalDiv .modal-product-meta{display:flex;flex-direction:column;',
    'align-items:center;justify-content:center}',
    '#OfferlyModalDiv #mobileModalProductTitle,#OfferlyModalDiv #mobileModalPrice{',
    'text-align:center!important}',
    '#OfferlyModalDiv #productTitleOfferModal{padding-bottom:0!important}',
    '#OfferlyModalDiv #offerlyModalHeader{margin-bottom:6px}',
    '#OfferlyModalDiv #modalTextInformation{margin-bottom:6px;font-size:13px;line-height:1.35}',
    '#OfferlyModalDiv .offerInput{padding-top:8px;padding-bottom:8px}',
    '#OfferlyModalDiv #offerNameInputInput,#OfferlyModalDiv #offerEmailInput,',
    '#OfferlyModalDiv #offerQuanitityInput{height:auto;min-height:0;margin-bottom:2px}',
    '#tlPriceLabel{margin:10px 0 6px}',
    '#tlPwycPills{gap:6px;margin:0 0 6px}',
    '#tlPwycPills .tl-pill{min-height:48px;font-size:17px}',
    '#tlPwycPills .tl-pill.tl-request{min-height:42px;font-size:14px}',
    '#tlPifLink{font-size:12px;padding:6px 8px}',
    '#OfferlyModalDiv #submitOfferButton{margin-top:8px}',
    '}',
  ].join('');
  document.head.appendChild(style);
}

// Two free-field modes. 'offer' = the "Other amount" path (Offerly offer,
// review framing when the amount lands below the auto-approve floor).
// 'pif' = Pay It Forward (amount above list, goes straight to cart
// with the overage as $1 PIF units). Each gets its own label, placeholder,
// button text, and live helper line so the customer always knows which deal
// they're making.
function tlProductTitle() {
  const el = document.getElementById('productTitleOfferModal') || document.getElementById('modalProductTitle');
  return el ? el.textContent.trim() : 'your item';
}

function tlUpdatePifHelper(amountInput) {
  const helper = document.getElementById('tlPifHelper');
  if (!helper) return;
  const row = document.getElementById('tlPwycPills');
  const mode = row && row.dataset.tlMode === 'pif' ? 'pif' : 'offer';
  const price = tlModalUnitPrice();
  if (price === null) { helper.textContent = ''; return; }
  const listTotal = price * tlQty();
  const amount = parseFloat(amountInput.value);
  const over = isFinite(amount) ? Math.round(amount - listTotal) : NaN;
  if (mode === 'pif') {
    if (!isFinite(amount) || amount <= listTotal) {
      helper.innerHTML = 'Enter any amount above <b>$' + listTotal + '</b> and the difference is paid forward.';
    } else {
      helper.innerHTML = '<b>$' + amount + '</b> = ' + tlProductTitle() + ' at <b>$' + listTotal + '</b> + <b>$' + over + '</b> paid forward. Thank you.';
    }
  } else if (isFinite(amount) && over > 0) {
    helper.innerHTML = 'That’s above full price. <b>$' + over + '</b> will be added as Pay It Forward.';
  } else {
    helper.textContent = '';
  }
}

function tlSetMode(mode, amountInput) {
  const row = document.getElementById('tlPwycPills');
  const wrap = amountInput.closest('div');
  if (!row || !wrap) return;
  row.dataset.tlMode = mode;
  wrap.classList.toggle('tl-pif-mode', mode === 'pif');
  const label = wrap.querySelector('label');
  const submitBtn = document.getElementById('submitOfferButton');
  let helper = document.getElementById('tlPifHelper');
  if (!helper) {
    helper = document.createElement('p');
    helper.id = 'tlPifHelper';
    wrap.parentElement.insertBefore(helper, wrap.nextSibling);
  }
  if (mode === 'pif') {
    if (label) label.textContent = 'Pay It Forward';
    const listTotal = (tlModalUnitPrice() || 0) * tlQty();
    amountInput.placeholder = 'More than ' + (listTotal ? '$' + listTotal : 'full price');
    if (submitBtn) submitBtn.textContent = 'Add To Cart';
  } else {
    if (label) label.textContent = 'Offer';
    amountInput.placeholder = 'Enter your offer';
    if (submitBtn && submitBtn.textContent.trim() === 'Add To Cart') submitBtn.textContent = 'Submit Offer';
  }
  tlUpdatePifHelper(amountInput);
}

// Offerly rewrites the modal price as "<s>$36</s><br>$29 (19.44% off)" on
// every amount change — cramped, red, and decimal-happy. Reformat to one
// clean line: struck list price, the offer price, and a whole-number percent
// in muted type. The struck list price stays FIRST so tlModalUnitPrice's
// first-$ regex keeps reading the real list price. In PIF mode the discount
// line makes no sense (the helper explains the split), so show plain list.
function tlFormatPrice(el) {
  if (!el) return;
  // Parse what Offerly has rendered RIGHT NOW; the cache is only a fallback for
  // the case where Offerly replaces the price text outright instead of
  // appending to it. Preferring the cache would pin a stale price after a
  // variant change, and self-perpetuate, because this function writes back into
  // the same element tlModalUnitPrice reads.
  const m = el.textContent.match(/\$\s*([\d,]+(?:\.\d{1,2})?)/);
  const parsed = m ? parseFloat(m[1].replace(/,/g, '')) : NaN;
  // An explicit zero is Offerly's transient rebuild state, not a price. Bail
  // rather than papering over it with the cache: falling back here would write
  // the PREVIOUS variant's price into the DOM as if it were valid and defeat
  // the zero guard this file just added, leaving helpers and tlPifIntercept
  // sizing against a stale list price until Offerly renders again. The cache is
  // for "no figure present at all", not for "a figure that says zero".
  if (m && !(parsed > 0)) return;
  const listed = parsed > 0 ? parsed : parseFloat(el.dataset.tlListPrice);

  // Pay It Forward is reachable TWO ways: the explicit link (mode 'pif'), and
  // simply typing an above-list amount into the offer field, which
  // tlPifIntercept routes to the PIF cart path while the mode stays 'offer'.
  // B2a made the second route ordinary by keeping the field visible. Offerly's
  // "Offer cannot exceed price" is wrong on BOTH, so key off the amount as well
  // as the mode.
  const row = document.getElementById('tlPwycPills');
  const amtEl = document.querySelector('input.offerInput[name="offerAmount"]');
  const typed = amtEl ? parseFloat(amtEl.value) : NaN;
  const aboveList = listed > 0 && typed > listed * tlQty();

  if ((row && row.dataset.tlMode === 'pif') || aboveList) {
    // Deliberately AHEAD of the [data-tl-fmt] short-circuit below: if Offerly
    // appends its validation after our formatted node, that guard would see the
    // marker and skip this cleanup, leaving the wrong message on screen.
    // Writing only when the content differs keeps the MutationObserver from
    // looping. Compare structure, never serialized HTML: the browser reads
    // `<span data-tl-fmt>` back as `<span data-tl-fmt="">`, so a string compare
    // never matched, every write re-fired the observer, and the page froze the
    // moment the modal entered PIF mode (live 2026-08-20 to 2026-10-20).
    if (listed > 0) {
      el.dataset.tlListPrice = String(listed);
      const text = '$' + listed;
      const only = el.childNodes.length === 1 ? el.firstChild : null;
      const current = only && only.nodeType === 1 && only.hasAttribute('data-tl-fmt') && only.textContent === text;
      if (!current) el.innerHTML = '<span data-tl-fmt>' + text + '</span>';
    }
    return;
  }

  if (el.querySelector('[data-tl-fmt]')) return;
  const s = el.querySelector('s');
  if (!s) return;
  const orig = parseFloat((s.textContent.match(/[\d.]+/) || [])[0]);
  const offerMatch = el.textContent.replace(s.textContent, '').match(/\$([\d.,]+)/);
  if (!isFinite(orig) || !offerMatch) return;
  const offer = parseFloat(offerMatch[1].replace(/,/g, ''));
  if (!isFinite(offer) || offer >= orig) return;
  const pct = Math.round((1 - offer / orig) * 100);
  el.innerHTML =
    '<s data-tl-fmt style="color:#6b5d52;font-weight:400;font-size:14px">$' + orig + '</s> ' +
    '<span style="color:#e84e28;font-weight:700;font-size:19px">$' + offer + '</span> ' +
    '<span style="color:#6b5d52;font-size:13px;font-weight:400">(' + pct + '% off)</span>';
}

function tlWatchPrices() {
  ['modalPrice', 'mobileModalPrice'].forEach((id) => {
    const el = document.getElementById(id);
    if (!el || el.dataset.tlWatched === '1') return;
    el.dataset.tlWatched = '1';
    tlFormatPrice(el);
    new MutationObserver(() => tlFormatPrice(el)).observe(el, { childList: true, subtree: true });
  });
}

// The amount field is editable while a pill is selected, so a typed value
// that no longer matches the pressed pill must clear the selection. An empty
// value is left alone: that is the "Other amount" pill's own state.
function tlSyncPillSelection(amountInput) {
  const row = document.getElementById('tlPwycPills');
  if (!row) return;
  const typed = amountInput.value.trim();
  row.querySelectorAll('.tl-pill').forEach((p) => {
    // An empty field clears a pressed price pill — nothing is selected once
    // the amount is deleted — but leaves "Other amount" alone, because
    // empty IS that pill's own state (Codex P2).
    if (typed === '') {
      if (p.dataset.amt !== '') p.setAttribute('aria-pressed', 'false');
      return;
    }
    const matches = p.dataset.amt !== '' && p.dataset.amt === typed;
    p.setAttribute('aria-pressed', matches ? 'true' : 'false');
  });
}

// ---------------------------------------------------------------------------
// Microsoft Clarity instrumentation (project ansfd44v91).
//
// WHY: the B2 verdict could only ever see ORDERS. It showed PWYC volume falling
// 36% concentrated in $50-150 and multi-item carts, but nothing about what
// those shoppers did in the modal before they left — every causal claim was an
// inference. These tags make the next read a funnel instead.
//
// Clarity is loaded by the Microsoft Clarity app embed (plus the app's web
// pixel in checkout). Its stub queues calls made before the script lands, but
// the stub may not exist yet (embed disabled, script blocked), so every call is
// guarded. Nothing here
// is load-bearing for the modal: a Clarity outage must never break checkout.
//
// LIMIT worth stating: pwyc_submit means "we handed the submission off or we
// acted on it", NOT "an order was created". Offerly runs its own validation
// after the hand-off and that outcome is not visible to this script, so treat
// submit-to-order as a separate, unmeasured step.
//
// PII: bands and percentages only. Never the name, email, or a raw amount.
// Clarity caps a page at 128 tags; this adds five keys and three events.
// Set by the #offerButton click, cleared once that open has been resolved.
let tlPendingOpen = false;
// True ONLY when this modal open was actually counted. Every later funnel event
// is gated on it, so a submit can never be recorded without its own open.
let tlFunnelActive = false;
// Consent as it stood AT THE MOMENT OF THE CLICK. Consent is a property of when
// the interaction happened, not of when we got round to recording it — the
// modal takes up to 3s to build, and reading consent only at record time would
// let a mid-build opt-in retroactively legitimise a pre-consent click.
let tlOpenConsented = false;
// The cart band as it stood when this open was COUNTED. Both the open event and
// the submit event use this same snapshot, so the per-band funnel has a matched
// numerator and denominator. Without it the numerator (submit, current band)
// and the denominator (open, no band) describe different populations and no
// per-band rate can be computed at all — which was the whole point of the patch.
let tlOpenBand = null;
// One submit per counted open. Offerly can reject an offer after our capture
// listener has already run, and a PIF cart add can fail — in both cases the
// modal stays open and the shopper retries. Without this the submit events
// count ATTEMPTS against an open counted once, so the per-band rate could
// exceed 1 and open-minus-submit would no longer be abandonment.
let tlSubmittedThisOpen = false;

// Clarity's own snippet installs a stub that queues calls until the script
// lands, and the script drains window.clarity.q on arrival. But the stub is not
// guaranteed (embed toggled off, script blocked), so it may not exist when the
// modal opens.
// Dropping those calls would lose the first pwyc_modal_open while still
// recording a later submission - understating opens and flattering the
// conversion rate, which is the one direction of bias that matters here.
// Installing the same queueing stub is safe and idiomatic. Capped so a visitor
// who never loads Clarity (consent declined, no qualifying interaction) cannot
// grow the queue without bound.
// Consent gate, FAIL CLOSED. The Clarity app embed carries Shopify consent
// wiring, and a queue that buffers pre-consent calls would
// hand them to Clarity the moment a visitor opts in — transmitting interactions
// that happened BEFORE consent and mislabelling them as post-consent telemetry.
// So: no consent, no queue, no send, and nothing retained to send later. If the
// privacy API has not loaded yet we cannot confirm consent, which counts as NO.
function tlAnalyticsAllowed() {
  try {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    return !!(cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed());
  } catch (e) {
    return false;
  }
}

// Returns true only if the call was actually accepted, so callers can avoid
// clearing state on a send that silently went nowhere.
function tlClarityCall() {
  try {
    if (!tlAnalyticsAllowed()) return false;
    if (typeof window.clarity !== 'function') {
      // Clarity's own stub, so calls made between consent and script arrival are
      // not lost. Capped: a visitor whose loader never fires cannot grow it.
      window.clarity = function () {
        const q = (window.clarity.q = window.clarity.q || []);
        if (q.length < 200) q.push(arguments);
      };
    }
    window.clarity.apply(window, arguments);
    return true;
  } catch (e) {
    return false; /* telemetry must never break the modal */
  }
}

function tlClaritySet(key, value) {
  if (!tlFunnelActive) return false;
  return tlClarityCall('set', key, String(value));
}

// bypassFunnelGate is for the modal-open event itself, which is what DECIDES
// whether the funnel is active. Everything else stays gated.
function tlClarityEvent(name, bypassFunnelGate) {
  if (!bypassFunnelGate && !tlFunnelActive) return false;
  return tlClarityCall('event', name);
}

// Bands chosen to match the list-value buckets the verdict reads are graded on,
// so a Clarity segment lines up with the order-level table without re-cutting.
function tlCartBand(listTotal) {
  if (!(listTotal > 0)) return 'unknown';
  if (listTotal < 30) return 'lt30';
  if (listTotal < 50) return '30-49';
  if (listTotal < 75) return '50-74';
  if (listTotal < 100) return '75-99';
  if (listTotal < 150) return '100-149';
  return '150plus';
}

function tlQtyBand(qty) {
  if (qty >= 5) return '5plus';
  if (qty >= 3) return '3-4';
  return String(qty);
}

// Clarity custom tags are SESSION/PAGE metadata, not properties attached to an
// event, so filtering pwyc_submit by the pwyc_cart_band TAG cannot tell you the
// band at THAT submission — the tag may have moved since. Cart size is the whole
// finding, so it is encoded into the event NAME, which Clarity can filter
// reliably. Six bands, so seven event names total including the plain one; the
// plain pwyc_submit stays as the denominator. Pill and quantity remain session
// tags for coarse segmentation only — that limit is real and is documented
// rather than hidden.
function tlClaritySubmitEvents() {
  // Retries are still worth seeing — repeated failed submissions are their own
  // usability signal — but they are counted on a separate name so they cannot
  // contaminate the rate.
  if (tlSubmittedThisOpen) {
    tlClarityEvent('pwyc_submit_retry');
    return;
  }
  // Set only AFTER the primary event is accepted. If consent was withdrawn or
  // the send threw, marking it here would leave the counted open with no submit
  // at all and downgrade every later attempt to a retry.
  if (!tlClarityEvent('pwyc_submit')) return;
  tlSubmittedThisOpen = true;
  // Deliberately the OPENING band, not the band at this instant. A shopper who
  // opens a $40 cart and raises quantity to 3 would otherwise be counted as an
  // open in 30-49 and a submit in 100-149, so neither band's rate would be
  // right. Matching both ends to the opening snapshot answers the actual
  // question — of modals opened against a cart this size, how many submitted.
  const price = tlModalUnitPrice();
  const band = tlOpenBand || (price > 0 ? tlCartBand(price * tlQty()) : null);
  if (band) tlClarityEvent('pwyc_submit_cart_' + band);
  // Resolved ONCE, here, rather than re-set on every click and keystroke. The
  // Clarity docs do not state whether a repeated set on one key replaces or
  // accumulates, so this is written to be correct either way: under replace it
  // is the submitted pill, under accumulate it is the set of submitted pills,
  // one per submission. Both are meaningful; a tag re-written on every state
  // change would be meaningless under accumulate.
  const row = document.getElementById('tlPwycPills');
  const hit = row && row.querySelector('.tl-pill[aria-pressed="true"]');
  const listTotal = price > 0 ? price * tlQty() : 0;
  if (hit && hit.dataset.amt === '') {
    tlClaritySet('pwyc_pill', 'request');
  } else if (hit && listTotal > 0) {
    tlClaritySet('pwyc_pill', Math.round((parseFloat(hit.dataset.amt) / listTotal) * 100));
  } else if (listTotal > 0) {
    const amtEl = document.querySelector('input.offerInput[name="offerAmount"]');
    const typed = amtEl ? parseFloat(amtEl.value) : NaN;
    tlClaritySet('pwyc_pill', typed > 0 ? 'custom_' + Math.round((typed / listTotal) * 100) : 'custom');
  }
}

// Called ONCE per counted modal open, to record the OPENING CONTEXT. Not on
// every state change: the Clarity docs do not specify whether a repeated set on
// the same key replaces or accumulates, and a tag rewritten on every keystroke
// is unreadable if it accumulates. Anything needing point-in-time accuracy is an
// EVENT instead — see tlClaritySubmitEvents.
function tlClarityTagModal() {
  const price = tlModalUnitPrice();
  const qty = tlQty();
  tlClaritySet('pwyc_qty_band', tlQtyBand(qty));
  if (price > 0) tlClaritySet('pwyc_cart_band', tlCartBand(price * qty));
}

function tlSelectPill(pill, amountInput, viaKeyboard, userInitiated) {
  const pills = pill.parentElement.querySelectorAll('.tl-pill');
  pills.forEach((p) => p.setAttribute('aria-pressed', p === pill ? 'true' : 'false'));
  const wrap = amountInput.closest('div') || amountInput;
  tlSetMode('offer', amountInput);
  // Which rung was taken, as a percent of list rather than a dollar amount, so
  // the segment is comparable across products. The request pill is the one the
  // B2 read suspected of costing the larger carts, so it gets its own event.
  // Only on genuine activation. tlRenderPills re-selects the preserved pill
  // after a quantity change, and counting that would let quantity edits inflate
  // the very event this instrumentation exists to measure.
  if (pill.dataset.amt === '' && userInitiated) tlClarityEvent('pwyc_request_path');
  if (pill.dataset.amt === '') {
    wrap.style.display = '';
    // Position the $ against the now-visible input (offsets are 0 while the
    // wrap is display:none, so this can't happen at insert time).
    const dollar = wrap.querySelector('.tl-dollar');
    if (dollar) {
      dollar.style.top = amountInput.offsetTop + amountInput.offsetHeight / 2 + 'px';
    }
    amountInput.value = '';
    // No focus() here: auto-focus pops the iOS keyboard, and iOS scrolls the
    // page to the input — dragging the modal up and exposing the PDP behind
    // it (Seth's phone, 2026-08-09). Touch users tap the field themselves;
    // keyboard/AT activation (event.detail === 0) still routes focus so the
    // revealed field is reachable (Codex P2).
    if (viaKeyboard) amountInput.focus();
  } else {
    // B2a (2026-08-19): the amount field stays VISIBLE with the pill's amount
    // in it, instead of being hidden. Hiding it made typing a custom number a
    // second click behind "Submit A Request", and the Aug 10-16 order-level
    // read showed $50-150 carts down ~76% and 3-4 unit carts down 78% while
    // single-unit carts only tracked store-wide softness. Pills stay one-tap
    // shortcuts; the field stays a first-class input.
    wrap.style.display = '';
    const dollarSel = wrap.querySelector('.tl-dollar');
    if (dollarSel) {
      dollarSel.style.top = amountInput.offsetTop + amountInput.offsetHeight / 2 + 'px';
    }
    amountInput.value = pill.dataset.amt;
  }
  // Let Offerly's own listeners see the new value so its price/savings
  // readout updates exactly as it does when a customer types.
  amountInput.dispatchEvent(new Event('input', { bubbles: true }));
  amountInput.dispatchEvent(new Event('keyup', { bubbles: true }));
}

function tlRenderPills(amountInput) {
  const amounts = tlPillAmounts();
  if (!amounts) return;
  tlInjectStylesOnce();

  let row = document.getElementById('tlPwycPills');
  const fresh = !row;
  if (fresh) {
    row = document.createElement('div');
    row.id = 'tlPwycPills';
    const wrap = amountInput.closest('div') || amountInput;
    // Section label styled with Offerly's own label classes so it matches
    // Full Name / Email / Quantity exactly.
    const sectionLabel = document.createElement('label');
    sectionLabel.id = 'tlPriceLabel';
    sectionLabel.className = 'offerly-text offerly-input-label';
    sectionLabel.textContent = 'Choose Your Price';
    wrap.parentElement.insertBefore(sectionLabel, wrap);
    wrap.parentElement.insertBefore(row, wrap);
  }

  // Remember selection across re-renders by index, default = full price.
  // Since B2a the field is editable while a pill is selected, so "no pill
  // pressed" is now a real state: the customer typed their own amount. That
  // must NOT fall through to index 0, which would silently overwrite their
  // offer with the recalculated full price (Codex P1).
  const prev = row.querySelector('.tl-pill[aria-pressed="true"]');
  const custom = !prev && !fresh && amountInput.value.trim() !== '';
  const prevIdx = prev ? [...row.children].indexOf(prev) : 0;

  row.textContent = '';
  amounts.forEach((amt, i) => {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'tl-pill';
    pill.dataset.amt = String(amt);
    pill.textContent = '$' + amt;
    pill.addEventListener('click', (e) => tlSelectPill(pill, amountInput, e.detail === 0, true));
    row.appendChild(pill);
  });
  const other = document.createElement('button');
  other.type = 'button';
  other.className = 'tl-pill tl-request';
  other.dataset.amt = '';
  // Label is "Other amount", not a verb phrase. Clarity, Aug 10-16: 76 sessions
  // took this control and reached Add to cart 28.9% of the time against 39.8%
  // for the preset path. It is the only non-price option in a row of prices and
  // it was worded like a terminal action, so replays show shoppers press it
  // expecting to have submitted, then hunt for the real button. It only clears
  // and reveals the amount field; "Submit Offer" is the submit.
  other.textContent = 'Other amount';
  other.addEventListener('click', (e) => tlSelectPill(other, amountInput, e.detail === 0, true));
  row.appendChild(other);

  if (!document.getElementById('tlPifLink')) {
    const pif = document.createElement('button');
    pif.type = 'button';
    pif.id = 'tlPifLink';
    pif.textContent = 'Want to pay it forward?';
    pif.addEventListener('click', (e) => {
      row.querySelectorAll('.tl-pill').forEach((p) => p.setAttribute('aria-pressed', 'false'));
      const wrap = amountInput.closest('div');
      wrap.style.display = '';
      const dollar = wrap.querySelector('.tl-dollar');
      if (dollar) dollar.style.top = amountInput.offsetTop + amountInput.offsetHeight / 2 + 'px';
      amountInput.value = '';
      // Clearing + dispatching resets Offerly's price line to plain list.
      amountInput.dispatchEvent(new Event('input', { bubbles: true }));
      amountInput.dispatchEvent(new Event('keyup', { bubbles: true }));
      tlSetMode('pif', amountInput);
      tlClarityEvent('pwyc_pay_it_forward');
      // Same keyboard-only focus rule as tlSelectPill.
      if (e.detail === 0) amountInput.focus();
    });
    row.parentElement.insertBefore(pif, row.nextSibling);
  }

  const typed = amountInput.value;
  if (custom) {
    // Leave every pill unpressed and hand the typed amount straight back, so
    // a quantity change never rewrites a custom offer. The wrap is already
    // visible on this path; setting it is belt-and-braces so a preserved
    // amount can never end up in a hidden field.
    const wrapCustom = amountInput.closest('div');
    if (wrapCustom) wrapCustom.style.display = '';
    row.querySelectorAll('.tl-pill').forEach((p) => p.setAttribute('aria-pressed', 'false'));
    amountInput.value = typed;
    amountInput.dispatchEvent(new Event('input', { bubbles: true }));
    amountInput.dispatchEvent(new Event('keyup', { bubbles: true }));
    return;
  }
  const keep = row.children[Math.min(prevIdx, row.children.length - 1)];
  tlSelectPill(keep, amountInput);
  if (keep.dataset.amt === '' && !fresh) {
    // Re-render triggered by a quantity change while the customer is in the
    // free field: keep their typed amount and don't steal focus. Dispatch
    // again after restoring so Offerly's savings/submission state tracks the
    // restored value, not the transient empty one (Codex P2).
    amountInput.value = typed;
    amountInput.dispatchEvent(new Event('input', { bubbles: true }));
    amountInput.dispatchEvent(new Event('keyup', { bubbles: true }));
    amountInput.blur();
  }
}

function tlCustomizeOfferModal() {
  const modal = document.getElementById('OfferlyModalDiv');
  if (!modal || getComputedStyle(modal).display === 'none') return;

  const nameInput = document.querySelector('input.offerInput[name="customerName"]');
  if (nameInput) nameInput.placeholder = 'First Name';

  const amountInput = document.querySelector('input.offerInput[name="offerAmount"]');
  if (!amountInput) return;

  if (!document.getElementById('tlPwycPills')) tlRenderPills(amountInput);
  // Offerly HIDES and reuses the modal DOM, so the pill row survives a close.
  // Keying the open event off row creation would count only the first open of
  // a session while still counting requests and submits from every later one,
  // leaving the denominator too low. Key it off the click that opened the modal
  // instead, and require the row to exist so a build still stuck on Offerly's
  // transient $0 cannot re-fire it on every 200ms poll tick.
  // Two requirements collide here and CONSENT WINS.
  //
  // The funnel needs every submit to have a matching open, which argues for
  // retrying an open that was refused. But the loader is interaction-gated, so
  // when the offer button IS the first interaction the privacy API may not have
  // resolved yet — and retrying after it resolves would transmit an interaction
  // that happened BEFORE consent was confirmed, which is exactly what the gate
  // exists to prevent.
  //
  // So an open that cannot be confirmed as consented at the moment it happened
  // is simply not measurable: it is dropped, never replayed, and tlFunnelActive
  // stays false so every later event for this open is suppressed too. That
  // costs some legitimate opens (consent-allowed but API not yet loaded) and
  // keeps the funnel internally consistent, which is the property that matters —
  // an undercount on both sides beats a submit with no open.
  // A valid price is required, not just the pill row. On a REOPEN the row
  // already exists, so without this the open could be counted during Offerly's
  // transient $0 rebuild — tlClarityTagModal would then omit pwyc_cart_band and
  // the per-band denominator would be short by exactly the opens that were
  // slowest to build. The click flag survives, so it is counted a tick later.
  if (tlPendingOpen && document.getElementById('tlPwycPills') && tlModalUnitPrice() > 0) {
    // Both the snapshot and the present state must allow it: the snapshot stops
    // a mid-build opt-in from legitimising a pre-consent click, and the live
    // check stops a mid-build withdrawal from being ignored.
    if (!tlOpenConsented || !tlAnalyticsAllowed()) {
      tlPendingOpen = false;
      tlFunnelActive = false;
    } else if (tlClarityEvent('pwyc_modal_open', true)) {
      tlPendingOpen = false;
      tlFunnelActive = true;
      const openPrice = tlModalUnitPrice();
      tlOpenBand = openPrice > 0 ? tlCartBand(openPrice * tlQty()) : null;
      if (tlOpenBand) tlClarityEvent('pwyc_open_cart_' + tlOpenBand);
      tlClarityTagModal();
    }
  }
  tlWatchPrices();

  // Fixed $ inside the free-entry field so nobody types the symbol.
  const wrap = amountInput.closest('div');
  if (wrap && !wrap.querySelector('.tl-dollar')) {
    wrap.classList.add('tl-amount-wrap');
    const dollar = document.createElement('span');
    dollar.className = 'tl-dollar';
    dollar.textContent = '$';
    wrap.insertBefore(dollar, amountInput);
    // Since B2a the wrap is visible on first render, so the marker is
    // centred here rather than waiting for a pill click — tlRenderPills runs
    // BEFORE this insertion, so its own positioning pass finds no marker and
    // later passes skip an existing one (Codex P2).
    dollar.style.top = amountInput.offsetTop + amountInput.offsetHeight / 2 + 'px';
    // Offerly's wrap holds a label above the input, so the $ is centered
    // against the input itself (in tlSelectPill, once visible). Padding set
    // inline to outrank Offerly's stylesheet.
    amountInput.style.paddingLeft = '26px';
  }

  // Submit button reads "Submit Offer".
  const submitBtn = [...modal.querySelectorAll('button, input[type="submit"]')].find((b) =>
    /^(send offer|submit)$/i.test(((b.textContent || b.value) || '').trim())
  );
  if (submitBtn) {
    if (submitBtn.tagName === 'INPUT') submitBtn.value = 'Submit Offer';
    else submitBtn.textContent = 'Submit Offer';
  }

  if (amountInput.dataset.tlWired !== '1') {
    amountInput.dataset.tlWired = '1';
    amountInput.addEventListener('input', () => {
      tlSyncPillSelection(amountInput);
      tlUpdatePifHelper(amountInput);
    });
    const qtyInput = document.querySelector('input.offerInput[name="quantity"]');
    if (qtyInput) {
      // Quantity changes re-price every pill (offers are totals) and rewrite
      // the selected pill's value; a customer typing in the free field is
      // never touched — that path has no pill selected with an amount.
      qtyInput.addEventListener('input', () => {
        const row = document.getElementById('tlPwycPills');
        if (row) tlRenderPills(amountInput);
      });
    }
  }
}

// Pay It Forward interception: if the free-field amount exceeds the list
// total, the click never reaches Offerly — we add the product at full price
// plus (amount - list) units of the $1 PIF variant and go to the cart.
// Amount exactly at list or below stays a normal Offerly offer.
function tlPifIntercept(event) {
  const modal = document.getElementById('OfferlyModalDiv');
  if (!modal || getComputedStyle(modal).display === 'none') return false;
  const btn = event.target.closest && event.target.closest('#submitOfferButton');
  if (!btn) return false;
  const amountInput = document.querySelector('input.offerInput[name="offerAmount"]');
  const price = tlModalUnitPrice();
  if (!amountInput || price === null) return false;
  const amount = parseFloat(amountInput.value);
  const listTotal = price * tlQty();
  const row = document.getElementById('tlPwycPills');
  const pifMode = row && row.dataset.tlMode === 'pif';
  if (!isFinite(amount) || amount <= listTotal) {
    if (pifMode) {
      // PIF mode never submits an Offerly offer — nudge instead.
      event.preventDefault();
      event.stopPropagation();
      tlUpdatePifHelper(amountInput);
      amountInput.focus();
      // A blocked click is NOT a submission - the shopper is still sitting in
      // the modal. Counting it would hide abandonment, the very number this
      // instrumentation exists to expose. Tracked separately because repeated
      // blocks are themselves a usability signal.
      tlClarityEvent('pwyc_submit_blocked');
      return true;
    }
    // Ordinary offer, handed off to Offerly.
    tlClaritySubmitEvents();
    return false;
  }

  // Variant to buy = the PDP form's current selection.
  const variantField = document.querySelector('form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]');
  if (!variantField || !variantField.value) {
    // No usable variant control, so this above-list submission falls through to
    // Offerly rather than the PIF cart path. It is still a submission and must
    // be counted, or a product whose variant control does not match the query
    // produces a counted open with no submit.
    tlClaritySubmitEvents();
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  const over = Math.round(amount - listTotal);
  btn.textContent = 'Adding…';
  const items = [{ id: parseInt(variantField.value, 10), quantity: tlQty() }];
  if (over > 0) items.push({ id: TL_PIF_VARIANT_ID, quantity: over });
  tlClaritySubmitEvents();
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  })
    .then((r) => {
      if (!r.ok) throw new Error('cart add failed ' + r.status);
      window.location.href = '/cart';
    })
    .catch(() => {
      btn.textContent = pifMode ? 'Add To Cart' : 'Submit Offer';
      alert("We couldn't add that to your cart. Please try again.");
    });
  return true;
}

// Capture phase: Offerly's own click handler stops propagation, so a bubble
// listener never fires (verified live 2026-07-20).
document.addEventListener(
  'click',
  (event) => {
    if (tlPifIntercept(event)) return;
    if (!event.target.closest || !event.target.closest('#offerButton')) return;
    // Marks a fresh open. Offerly reuses the modal DOM, so this click is the
    // only reliable signal that a NEW open happened. The funnel is inactive
    // until this open is actually counted.
    tlPendingOpen = true;
    tlFunnelActive = false;
    tlOpenBand = null;
    tlSubmittedThisOpen = false;
    tlOpenConsented = tlAnalyticsAllowed();
    const start = Date.now();
    const timer = setInterval(() => {
      tlCustomizeOfferModal();
      if (Date.now() - start > 3000) clearInterval(timer);
    }, 200);
  },
  true
);
